/**
 * Fixation detection using spatial clustering
 *
 * This module uses a spatial clustering approach instead of traditional I-DT:
 * 1. Divide UV space into a grid of cells
 * 2. Count gaze points in each cell
 * 3. Cells with point count above threshold become fixation candidates
 * 4. Merge adjacent high-density cells into fixation regions
 *
 * This approach aligns with heatmap visualization - areas with high point density
 * (shown as hot colors on heatmap) will have corresponding fixation markers.
 *
 * The traditional I-DT algorithm fails when users scan areas repeatedly without
 * pausing - creating high heatmap density but no "classical" fixations.
 */

import type {
  GazePoint,
  Direction3D,
  Fixation,
  Saccade,
  ScanpathData,
  FixationDetectionConfig,
  UVCoordinates,
} from "../types/gazeHeatmapTypes";
import {
  directionToEquirectangularUV,
  directionToEquirectangularUV_Video360,
} from "./coordinateConversion";

/**
 * Calculate the angular distance between two 3D direction vectors in degrees
 */
export function angularDistance(dir1: Direction3D, dir2: Direction3D): number {
  // Normalize vectors
  const len1 = Math.sqrt(dir1.x * dir1.x + dir1.y * dir1.y + dir1.z * dir1.z);
  const len2 = Math.sqrt(dir2.x * dir2.x + dir2.y * dir2.y + dir2.z * dir2.z);

  if (len1 === 0 || len2 === 0) return 0;

  const nx1 = dir1.x / len1;
  const ny1 = dir1.y / len1;
  const nz1 = dir1.z / len1;

  const nx2 = dir2.x / len2;
  const ny2 = dir2.y / len2;
  const nz2 = dir2.z / len2;

  // Dot product gives cos(angle)
  let dot = nx1 * nx2 + ny1 * ny2 + nz1 * nz2;

  // Clamp to [-1, 1] to handle numerical errors
  dot = Math.max(-1, Math.min(1, dot));

  // Return angle in degrees
  return Math.acos(dot) * (180 / Math.PI);
}

/**
 * Calculate the dispersion (maximum angular distance) of a set of points
 */
export function calculateDispersion(points: GazePoint[]): number {
  if (points.length < 2) return 0;

  let maxDispersion = 0;

  for (let i = 0; i < points.length; i++) {
    for (let j = i + 1; j < points.length; j++) {
      const distance = angularDistance(points[i].direction, points[j].direction);
      if (distance > maxDispersion) {
        maxDispersion = distance;
      }
    }
  }

  return maxDispersion;
}

/**
 * Calculate the centroid (average direction) of a set of gaze points
 *
 * IMPORTANT: Each direction is normalized before averaging to ensure
 * consistent weighting. This matches how heatmapGeneration.ts processes
 * points (via directionToEquirectangularUV which normalizes internally).
 */
export function calculateCentroid(points: GazePoint[]): Direction3D {
  if (points.length === 0) {
    return { x: 0, y: 0, z: 1 }; // Default forward
  }

  let sumX = 0;
  let sumY = 0;
  let sumZ = 0;

  for (const point of points) {
    const { x, y, z } = point.direction;

    // Normalize each direction before averaging
    // This ensures all points contribute equally regardless of vector magnitude
    const len = Math.sqrt(x * x + y * y + z * z);
    if (len > 0) {
      sumX += x / len;
      sumY += y / len;
      sumZ += z / len;
    }
  }

  // Average
  const avgX = sumX / points.length;
  const avgY = sumY / points.length;
  const avgZ = sumZ / points.length;

  // Normalize the result
  const length = Math.sqrt(avgX * avgX + avgY * avgY + avgZ * avgZ);
  if (length === 0) {
    return { x: 0, y: 0, z: 1 };
  }

  return {
    x: avgX / length,
    y: avgY / length,
    z: avgZ / length,
  };
}

/**
 * Detect fixations using spatial clustering with cell merging
 *
 * This approach creates fixations based on spatial density of gaze points,
 * aligning with what the heatmap shows. Areas with high point density
 * (hot colors on heatmap) will have corresponding fixation markers.
 *
 * The detection always finds ALL fixations. Filtering by "strength" (point count)
 * is done separately via filterFixationsByStrength() for display purposes.
 *
 * @param points - Array of gaze points
 * @param config - Detection configuration
 * @param sceneRotationY - Scene rotation for UV conversion (unused, kept for compatibility)
 * @param isVideo360 - Whether to use video 360 UV conversion (AVProVideo shader formula)
 * @returns Array of detected fixations with strength scores
 */
export function detectFixations(
  points: GazePoint[],
  _config: Partial<FixationDetectionConfig> = {},
  sceneRotationY = 0,
  isVideo360 = false,
): Fixation[] {
  if (points.length < 2) {
    return [];
  }

  // === FINE GRID ===
  // Use a finer grid to capture more potential fixations
  // 32x16 = 512 cells, each cell covers ~11° x 11° of the sphere
  const GRID_WIDTH = 32;
  const GRID_HEIGHT = 16;

  // === MERGE RADIUS FROM CONFIG ===
  // 0 = no merging, 1 = adjacent only, 2-4 = increasing merge distance
  const MERGE_RADIUS = _config.mergeRadius ?? 1;

  // Select the appropriate conversion function based on media type
  // Video 360 uses AVProVideo shader formula, Image 360 uses Custom360Cube shader formula
  const convertToUV = isVideo360
    ? directionToEquirectangularUV_Video360
    : directionToEquirectangularUV;

  // Convert all points to UV and create grid
  type CellData = {
    points: GazePoint[];
    uvSum: { u: number; v: number };
    cellX: number;
    cellY: number;
  };

  const grid: Map<string, CellData> = new Map();

  // Populate grid with points
  for (const point of points) {
    const uv = convertToUV(point.direction, sceneRotationY);

    // Calculate grid cell
    const cellX = Math.floor(uv.u * GRID_WIDTH) % GRID_WIDTH;
    const cellY = Math.floor(uv.v * GRID_HEIGHT) % GRID_HEIGHT;
    const cellKey = `${cellX},${cellY}`;

    if (!grid.has(cellKey)) {
      grid.set(cellKey, { points: [], uvSum: { u: 0, v: 0 }, cellX, cellY });
    }

    const cell = grid.get(cellKey);
    if (cell) {
      cell.points.push(point);
      cell.uvSum.u += uv.u;
      cell.uvSum.v += uv.v;
    }
  }

  // === THRESHOLD ===
  // Any cell with at least 1 point becomes a fixation candidate
  // Find cells that qualify as fixation candidates
  const qualifyingCells = Array.from(grid.entries())
    .filter(([, cell]) => cell.points.length >= 1)
    .map(([key, cell]) => ({ key, cell }));

  // === MERGE CELLS WITHIN RADIUS ===
  // Merge radius controlled by slider - higher radius = fewer fixations
  const cellToGroup = new Map<string, string>();
  const groupCells = new Map<string, Set<string>>();

  for (const { key } of qualifyingCells) {
    cellToGroup.set(key, key);
    groupCells.set(key, new Set([key]));
  }

  const findRoot = (key: string): string => {
    let root = cellToGroup.get(key);
    if (!root) return key;
    if (root !== key) {
      root = findRoot(root);
      cellToGroup.set(key, root);
    }
    return root;
  };

  const mergeGroups = (key1: string, key2: string): void => {
    const root1 = findRoot(key1);
    const root2 = findRoot(key2);
    if (root1 === root2) return;

    const group1 = groupCells.get(root1);
    const group2 = groupCells.get(root2);
    if (!group1 || !group2) return;

    if (group1.size >= group2.size) {
      Array.from(group2).forEach((k) => {
        group1.add(k);
        cellToGroup.set(k, root1);
      });
      groupCells.delete(root2);
    } else {
      Array.from(group1).forEach((k) => {
        group2.add(k);
        cellToGroup.set(k, root2);
      });
      groupCells.delete(root1);
    }
  };

  // Get neighbors within merge radius (Manhattan distance)
  const getNeighborsInRadius = (cellX: number, cellY: number, radius: number): string[] => {
    const neighbors: string[] = [];
    for (let dx = -radius; dx <= radius; dx++) {
      for (let dy = -radius; dy <= radius; dy++) {
        if (dx === 0 && dy === 0) continue;
        // Manhattan distance check
        if (Math.abs(dx) + Math.abs(dy) > radius) continue;
        const nx = (cellX + dx + GRID_WIDTH) % GRID_WIDTH; // Wrap horizontally
        const ny = cellY + dy;
        if (ny >= 0 && ny < GRID_HEIGHT) {
          neighbors.push(`${nx},${ny}`);
        }
      }
    }
    return neighbors;
  };

  // Effective radius for merging (0 = no merging, cells stay separate)
  const effectiveRadius = MERGE_RADIUS;

  for (const { cell } of qualifyingCells) {
    const key = `${cell.cellX},${cell.cellY}`;
    for (const neighborKey of getNeighborsInRadius(cell.cellX, cell.cellY, effectiveRadius)) {
      if (cellToGroup.has(neighborKey)) {
        mergeGroups(key, neighborKey);
      }
    }
  }

  // Build merged fixations
  const uniqueGroups = new Set<string>();
  for (const { key } of qualifyingCells) {
    uniqueGroups.add(findRoot(key));
  }

  type MergedData = { points: GazePoint[]; uvSum: { u: number; v: number }; earliestTime: number };
  const mergedFixations: MergedData[] = [];

  Array.from(uniqueGroups).forEach((rootKey) => {
    const cellKeys = groupCells.get(rootKey);
    if (!cellKeys) return;

    const data: MergedData = { points: [], uvSum: { u: 0, v: 0 }, earliestTime: Infinity };

    Array.from(cellKeys).forEach((cellKey) => {
      const entry = qualifyingCells.find((c) => c.key === cellKey);
      if (!entry) return;
      data.points.push(...entry.cell.points);
      data.uvSum.u += entry.cell.uvSum.u;
      data.uvSum.v += entry.cell.uvSum.v;
      const earliest = Math.min(...entry.cell.points.map((p) => p.time));
      if (earliest < data.earliestTime) data.earliestTime = earliest;
    });

    mergedFixations.push(data);
  });

  mergedFixations.sort((a, b) => a.earliestTime - b.earliestTime);

  // Find max point count for strength normalization
  const maxPointCount = Math.max(...mergedFixations.map((f) => f.points.length), 1);

  // Create fixation objects from merged groups
  const fixations: Fixation[] = [];

  for (let i = 0; i < mergedFixations.length; i++) {
    const data = mergedFixations[i];
    const cellPoints = data.points;

    // Sort points by time to get temporal info
    const sortedCellPoints = [...cellPoints].sort((a, b) => a.time - b.time);
    const startTime = sortedCellPoints[0].time;
    const endTime = sortedCellPoints[sortedCellPoints.length - 1].time;

    // Calculate centroid UV (average of all points)
    const centroidUV: UVCoordinates = {
      u: data.uvSum.u / cellPoints.length,
      v: data.uvSum.v / cellPoints.length,
    };

    // Calculate centroid direction
    const centroidDirection = calculateCentroid(cellPoints);

    // Duration is the time span of points
    const duration = (endTime - startTime) * 1000; // Convert to ms

    // Calculate strength (0-1) based on point count relative to max
    const strength = cellPoints.length / maxPointCount;

    fixations.push({
      id: i,
      startTime,
      endTime,
      duration: Math.max(duration, 1), // Ensure non-zero duration
      centroidUV,
      pointCount: cellPoints.length,
      centroidDirection,
      strength,
    });
  }

  return fixations;
}

/**
 * Filter fixations by strength threshold
 *
 * @param fixations - All detected fixations
 * @param threshold - Minimum strength (0-1) to display. 0 = show all, 1 = strongest only
 * @returns Filtered fixations sorted by time, with recalculated IDs
 */
export function filterFixationsByStrength(fixations: Fixation[], threshold: number): Fixation[] {
  // Filter by threshold
  const filtered = fixations.filter((f) => f.strength >= threshold);

  // Sort by start time to maintain temporal order
  filtered.sort((a, b) => a.startTime - b.startTime);

  // Re-assign IDs to maintain consecutive numbering
  return filtered.map((f, index) => ({
    ...f,
    id: index,
  }));
}

/**
 * Calculate saccades between fixations (ordered by time)
 *
 * Saccades connect fixations 1→2, 2→3, etc. showing the temporal path
 * of visual attention.
 *
 * @param fixations - Array of fixations ordered by time
 * @returns Array of saccades
 */
export function calculateSaccades(fixations: Fixation[]): Saccade[] {
  const saccades: Saccade[] = [];

  for (let i = 0; i < fixations.length - 1; i++) {
    const from = fixations[i];
    const to = fixations[i + 1];

    // Calculate angular amplitude (distance between fixation centers)
    const amplitude = angularDistance(from.centroidDirection, to.centroidDirection);

    saccades.push({
      fromFixationId: from.id,
      toFixationId: to.id,
      duration: 0, // Not meaningful with spatial clustering
      amplitude,
      startUV: from.centroidUV,
      endUV: to.centroidUV,
    });
  }

  return saccades;
}

/**
 * Generate complete scanpath data from gaze points
 *
 * @param points - Array of gaze points
 * @param config - Fixation detection configuration
 * @param sceneRotationY - Scene rotation for UV conversion (unused, kept for compatibility)
 * @param isVideo360 - Whether to use video 360 UV conversion (AVProVideo shader formula)
 * @returns Complete scanpath data
 */
export function generateScanpath(
  points: GazePoint[],
  config: Partial<FixationDetectionConfig> = {},
  sceneRotationY = 0,
  isVideo360 = false,
): ScanpathData {
  const startTime = performance.now();

  // Detect ALL fixations
  const allFixations = detectFixations(points, config, sceneRotationY, isVideo360);

  // Filter by display threshold (sensitivity slider)
  // displayThreshold: 0 = show all, 1 = only strongest
  const displayThreshold = config.displayThreshold ?? 0;
  const fixations = filterFixationsByStrength(allFixations, displayThreshold);

  // Calculate saccades between filtered fixations
  const saccades = calculateSaccades(fixations);

  // Calculate statistics
  const fixationCount = fixations.length;

  // Total fixation duration
  let totalFixationDuration = 0;
  for (const fixation of fixations) {
    totalFixationDuration += fixation.duration;
  }

  // Average fixation duration
  const averageFixationDuration = fixationCount > 0 ? totalFixationDuration / fixationCount : 0;

  // Total session duration (from first to last point)
  const sortedPoints = [...points].sort((a, b) => a.time - b.time);
  const totalDuration =
    sortedPoints.length > 1
      ? (sortedPoints[sortedPoints.length - 1].time - sortedPoints[0].time) * 1000
      : 0;

  // Fixation ratio (time spent in fixations / total time)
  const fixationRatio = totalDuration > 0 ? totalFixationDuration / totalDuration : 0;

  // Average saccade amplitude
  let totalSaccadeAmplitude = 0;
  for (const saccade of saccades) {
    totalSaccadeAmplitude += saccade.amplitude;
  }
  const averageSaccadeAmplitude = saccades.length > 0 ? totalSaccadeAmplitude / saccades.length : 0;

  const processingTime = performance.now() - startTime;

  return {
    fixations,
    saccades,
    fixationRatio: Math.min(1, fixationRatio), // Cap at 1
    fixationCount,
    averageFixationDuration,
    averageSaccadeAmplitude,
    processingTime,
  };
}

/**
 * Get fixations visible at a specific time (for video sync)
 *
 * @param scanpath - Full scanpath data
 * @param currentTime - Current video time in seconds
 * @param historyDuration - How many seconds of history to show (0 = only current)
 * @returns Filtered fixations and saccades
 */
export function getFixationsAtTime(
  scanpath: ScanpathData,
  currentTime: number,
  historyDuration = 5,
): { fixations: Fixation[]; saccades: Saccade[] } {
  const windowStart = currentTime - historyDuration;
  const windowEnd = currentTime;

  // Filter fixations that overlap with the time window
  const visibleFixations = scanpath.fixations.filter(
    (f) => f.endTime >= windowStart && f.startTime <= windowEnd,
  );

  // Sort visible fixations by start time
  const sortedFixations = [...visibleFixations].sort((a, b) => a.startTime - b.startTime);

  // Create saccades between consecutive visible fixations (sorted by time)
  // This ensures lines are drawn even if original fixations weren't consecutive
  const visibleSaccades: Saccade[] = [];
  for (let i = 0; i < sortedFixations.length - 1; i++) {
    const from = sortedFixations[i];
    const to = sortedFixations[i + 1];

    visibleSaccades.push({
      fromFixationId: from.id,
      toFixationId: to.id,
      duration: 0,
      amplitude: angularDistance(from.centroidDirection, to.centroidDirection),
      startUV: from.centroidUV,
      endUV: to.centroidUV,
    });
  }

  return {
    fixations: visibleFixations,
    saccades: visibleSaccades,
  };
}
